import React, { useState } from "react";
import { message as AntdAlert } from "antd";
import axios from "axios";
import { baseURL } from "../../../globalUtils/axiosConfig";

export const UseTemplateFields = () => {
  const [inputFields, setInputFields] = useState([]);
  const [fieldCounter, setFieldCounter] = useState(0);

  // Format fields for API submission
  const getFormattedFields = () => {
    return inputFields
      .filter(field => field.field_type !== 'choose' && field.name?.trim()) // Exclude 'choose' type and empty names
      .map(field => {
        // Map field types to match backend expectations
        let fieldType = field.field_type;
        if (fieldType === 'select_field') {
          fieldType = 'selector';
        } else if (fieldType === 'text_field') {
          fieldType = 'text';
        } else if (fieldType === 'date_field') {
          fieldType = 'date';
        }
        
        return {
          name: field.name,
          type: fieldType,
          ...(field.field_type === 'select_field' && {
            options: (field.options || [])
              .filter(opt => opt.name?.trim()) // Only include options with names
              .map(opt => ({
                name: opt.name
              }))
          })
        };
      });
  };

  const createField = (item) => {
    const { name, field_type, id } = item;

    if (!name?.trim()) {
      return AntdAlert.error("Field name cannot be empty!");
    }
    // If this is an existing field (has an ID), update it
    if (id) {
      setInputFields(prev => 
        prev.map(field => 
          field.id === id 
            ? { 
                ...field, 
                name: name.trim(), 
                field_type,
                isEdit: false 
              } 
            : field
        )
      );
    } else {
      // This is a new field
      const newField = {
        ...item,
        id: `field_${Math.floor(Math.random() * 10000000)}`,
        isEdit: false,
        options: [],
        name: name.trim()
      };
      setInputFields(prev => [...prev, newField]);
    }
    return true;
  };

  const deleteField = (item) => {
    setInputFields(prev => prev.filter(field => field.id !== item.id));
  };

  const updateField = (item) => {
    const { name, field_type, id } = item;
    
    if (!name?.trim()) {
      return AntdAlert.error("Field name cannot be empty!");
    }
    
    setInputFields(prev => 
      prev.map(field => 
        field.id === id 
          ? { ...field, name, field_type, isEdit: false }
          : field
      )
    );
    return true;
  };

  const onEditClick = (item) => {
    setInputFields(prev => 
      prev.map(field => 
        field.id === item.id 
          ? { ...field, isEdit: true }
          : field
      )
    );
  };

  const createOption = (item) => {
    const { name, parentId, isEdit, ...rest } = item;
    
    if (!name?.trim()) {
      return AntdAlert.error("Option name cannot be empty!");
    }
    
    setInputFields(prev => 
      prev.map(field => {
        if (field.id === parentId) {
          // Remove any options that are in edit mode
          const filteredOptions = (field.options || []).filter(opt => !opt.isEdit);
          
          // Add the new option
          const newOption = {
            id: `opt_${Date.now()}`,
            name: name.trim(),
            isEdit: false
          };
          
          return {
            ...field,
            options: [...filteredOptions, newOption]
          };
        }
        return field;
      })
    );
    return true;
  };

  const updateOption = (item) => {
    const { name, template_field_id: parentId, id: optionId } = item;
    
    if (!name?.trim()) {
      return AntdAlert.error("Option name cannot be empty!");
    }
    
    setInputFields(prev => 
      prev.map(field => {
        if (field.id === parentId) {
          return {
            ...field,
            options: field.options.map(opt =>
              opt.id === optionId
                ? { ...opt, name: name.trim(), isEdit: false }
                : opt
            )
          };
        }
        return field;
      })
    );
    return true;
  };

  const deleteOption = (item) => {
    const { template_field_id: parentId, id: optionId } = item;
    
    setInputFields(prev => 
      prev.map(field => 
        field.id === parentId
          ? {
              ...field,
              options: field.options.filter(opt => opt.id !== optionId)
            }
          : field
      )
    );
  };

  const onEditClickOption = ({ template_field_id: parentId, id: optionId }) => {
    setInputFields(prev => 
      prev.map(field => 
        field.id === parentId
          ? {
              ...field,
              options: field.options.map(opt =>
                opt.id === optionId
                  ? { ...opt, isEdit: true }
                  : opt
              )
            }
          : field
      )
    );
  };

  return [
    inputFields,
    setInputFields,
    createField,
    deleteField,
    updateField,
    onEditClick,
    createOption,
    updateOption,
    deleteOption,
    onEditClickOption,
    getFormattedFields // Add this to expose the formatter
  ];
};
